import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// ── Eager imports — kept in the main bundle for the hottest routes.
// First paint on /, /property/:id, /wifi-access/:unitCode must stay
// snappy. Sentry flagged render-blocking payload on /wifi-access
// (issue 7458844302) — lazy-loading everything else below trims the
// initial JS the browser has to parse on those cold entrypoints.
import HomePage from "./pages/HomePage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import WiFiAccessPage from "./pages/WiFiAccessPage";
import WelcomePage from "./pages/WelcomePage";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { FEATURE_FLAGS } from "./config";

// ── Lazy-loaded routes — each becomes its own webpack chunk so cold
// visitors to /wifi-access never download the host/admin dashboard JS.
const BookingSuccessPage = lazy(() => import("./pages/BookingSuccessPage"));
const CaptivePortalPage = lazy(() => import("./pages/CaptivePortalPage"));
const BecomeHostPage = lazy(() => import("./pages/BecomeHostPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const CustomerSignupPage = lazy(() => import("./pages/CustomerSignupPage"));
const CustomerLoginPage = lazy(() => import("./pages/CustomerLoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const CustomerDashboardPage = lazy(() => import("./pages/CustomerDashboardPage"));
const BookingLookupPage = lazy(() => import("./pages/BookingLookupPage"));
const WishlistsPage = lazy(() => import("./pages/WishlistsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const LocationPropertiesPage = lazy(() => import("./pages/LocationPropertiesPage"));
const HostLoginPage = lazy(() => import("./pages/HostLoginPage"));
const HostSignupPage = lazy(() => import("./pages/HostSignupPage"));
const HostDashboardPage = lazy(() => import("./pages/HostDashboardPage"));
const HostMessagesPage = lazy(() => import("./pages/HostMessagesPage"));
const HostCancellationRequestsPage = lazy(() => import("./pages/HostCancellationRequestsPage"));
const ConciergePage = lazy(() => import("./pages/ConciergePage"));
const TripPlannerPage = lazy(() => import("./pages/TripPlannerPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const StudentsPage = lazy(() => import("./pages/StudentsPage"));
const MedicalPage = lazy(() => import("./pages/MedicalPage"));
const InsurancePage = lazy(() => import("./pages/InsurancePage"));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const AboutMontrealPage = lazy(() => import("./pages/AboutMontrealPage"));
const HostResponsiblyPage = lazy(() => import("./pages/HostResponsiblyPage"));
const MontrealAttractionsPage = lazy(() => import("./pages/MontrealAttractionsPage"));
const ThingsToDoMontrealPage = lazy(() => import("./pages/ThingsToDoMontrealPage"));
const AirCoverPage = lazy(() => import("./pages/AirCoverPage"));
const SafetyInformationPage = lazy(() => import("./pages/SafetyInformationPage"));
const CancellationOptionsPage = lazy(() => import("./pages/CancellationOptionsPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const NewsroomPage = lazy(() => import("./pages/NewsroomPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const FoundersLetterPage = lazy(() => import("./pages/FoundersLetterPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const InvestorsPage = lazy(() => import("./pages/InvestorsPage"));
const HostResourcesPage = lazy(() => import("./pages/HostResourcesPage"));
const MapTestPage = lazy(() => import("./pages/MapTestPage"));
const LogoPreviewPage = lazy(() => import("./pages/LogoPreviewPage"));
const ExperienceMontrealPage = lazy(() => import("./pages/ExperienceMontrealPage"));
const ExperienceMontrealComingSoonPage = lazy(() => import("./pages/ExperienceMontrealComingSoonPage"));
const CalendarTestPage = lazy(() => import("./pages/CalendarTestPage"));
const GuestProfilePage = lazy(() => import("./pages/GuestProfilePage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const HostAgreement = lazy(() => import("./pages/HostAgreement"));
const PublicHostProfilePage = lazy(() => import("./pages/PublicHostProfilePage"));

// Role-based layouts — only needed when a logged-in user lands on their
// dashboard, so lazy.
const CustomerLayout = lazy(() => import("./customer/layouts/CustomerLayout"));
const HostLayout = lazy(() => import("./host/layouts/HostLayout"));
const AdminLayout = lazy(() => import("./admin/layouts/AdminLayout"));

// Design previews — internal-only mockups. Never visited by real users.
const PreviewIndex = lazy(() => import("./pages/design-previews/PreviewIndex"));
const WelcomeMobileOptionsPreview = lazy(() => import("./pages/WelcomeMobileOptionsPreview"));
const ColorPalettePreview = lazy(() => import("./pages/ColorPalettePreview"));
const IconExplorerPage = lazy(() => import("./pages/IconExplorerPage"));
const WelcomeFinalPreview = lazy(() => import("./pages/WelcomeFinalPreview"));
const PaletteExtensionsPreview = lazy(() => import("./pages/PaletteExtensionsPreview"));
const GroupPage = lazy(() => import("./pages/GroupPage"));
const BecomePartnerPage = lazy(() => import("./pages/BecomePartnerPage"));
const PartnerApplyPage = lazy(() => import("./pages/PartnerApplyPage"));
const ExperiencesPage = lazy(() => import("./pages/ExperiencesPage"));
const ExperienceDetailPage = lazy(() => import("./pages/ExperienceDetailPage"));
const PartnerSetupPasswordPage = lazy(() => import("./pages/PartnerSetupPasswordPage"));
const PartnerClaimPage = lazy(() => import("./pages/PartnerClaimPage"));
const PartnerLoginPage = lazy(() => import("./pages/PartnerLoginPage"));
const BecomeExperiencePartnerPage = lazy(() => import("./pages/BecomeExperiencePartnerPage"));
const PartnerDashboardPage = lazy(() => import("./pages/PartnerDashboardPage"));
const DiningPage = lazy(() => import("./pages/DiningPage"));
const DiningCheckoutSuccessPage = lazy(() => import("./pages/DiningCheckoutSuccessPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const PreviewA_Editorial = lazy(() => import("./pages/design-previews/PreviewA_Editorial"));
const PreviewB_RealEstateTech = lazy(() => import("./pages/design-previews/PreviewB_RealEstateTech"));
const PreviewC_Earthy = lazy(() => import("./pages/design-previews/PreviewC_Earthy"));
const PreviewD_SwissMinimal = lazy(() => import("./pages/design-previews/PreviewD_SwissMinimal"));
const PreviewE_GlassBento = lazy(() => import("./pages/design-previews/PreviewE_GlassBento"));
const PreviewF_CoastalBlue = lazy(() => import("./pages/design-previews/PreviewF_CoastalBlue"));
const PreviewG_ElectricBlue = lazy(() => import("./pages/design-previews/PreviewG_ElectricBlue"));
const PreviewH_SoftSage = lazy(() => import("./pages/design-previews/PreviewH_SoftSage"));
const PreviewI_PalePeach = lazy(() => import("./pages/design-previews/PreviewI_PalePeach"));
const PreviewJ_PowderBlue = lazy(() => import("./pages/design-previews/PreviewJ_PowderBlue"));
const PreviewK_Linen = lazy(() => import("./pages/design-previews/PreviewK_Linen"));
const PreviewL_LightBlueCurrent = lazy(() => import("./pages/design-previews/PreviewL_LightBlueCurrent"));

// Brand-aware landing — selects Stays vs Marketplace based on hostname (Phase 3.0)
// Public marketplace gate (Coming Soon) — flip to LandingMarketplace on launch day.
const LandingMarketplace = lazy(() => import("./pages/LandingMarketplace"));
const LandingMarketplaceComingSoon = lazy(() => import("./pages/LandingMarketplaceComingSoon"));

import { BrandProvider } from './brand/useBrand';
import BrandSEO from './brand/BrandSEO';
import BrandLandingRouter from './brand/BrandLandingRouter';
import MarketplaceGate from './brand/MarketplaceGate';

function App() {
  return (
    <div className="App flex flex-col min-h-screen">
      <BrowserRouter>
        <BrandProvider>
        <BrandSEO />
        <ScrollToTop />

        <Suspense
          fallback={
            <div
              className="flex items-center justify-center min-h-screen bg-white"
              data-testid="route-suspense-fallback"
              aria-label="Loading page"
            >
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
        <Routes>
          {/* /marketplace-preview is OUTSIDE the gate — it's the
              private surface where the full marketplace landing is
              reviewable during the pre-launch period. */}
          <Route path="/marketplace-preview" element={<LandingMarketplace />} />

          {/* MarketplaceGate is now a passthrough (see MarketplaceGate.jsx —
              gate disabled 2026-07-03). Kept as the wrapper Route so we
              can re-enable a lockout window with a one-line edit if
              needed for maintenance / re-launch. */}
          <Route element={<MarketplaceGate />}>
          {/* Brand-aware landing: hostname (or ?brand= override) selects
              between the existing Homesbyli WelcomePage and the actual
              UCWonder marketplace landing (Coming Soon gate lifted). */}
          <Route path="/" element={
            <BrandLandingRouter
              stays={<WelcomePage />}
              marketplace={<LandingMarketplace />}
            />
          } />

          {/* AI Travel Concierge (M1 preview — link-only, no public CTA yet) */}
          <Route path="/concierge" element={<ConciergePage />} />
          <Route path="/plan" element={<TripPlannerPage />} />

          {/* Notifications inbox */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Design exploration — read-only previews (no Header/Footer) */}
          <Route path="/design-previews" element={<PreviewIndex />} />
          <Route path="/preview/welcome-mobile-options" element={<WelcomeMobileOptionsPreview />} />
          <Route path="/preview/color-palettes" element={<ColorPalettePreview />} />
          <Route path="/preview/icons" element={<IconExplorerPage />} />
          <Route path="/preview/welcome-final" element={<WelcomeFinalPreview />} />
          <Route path="/preview/palette-extensions" element={<PaletteExtensionsPreview />} />
          <Route path="/group/:slug" element={<GroupPage />} />
          <Route path="/become-a-partner" element={<BecomePartnerPage />} />
          <Route path="/partner/apply" element={<PartnerApplyPage />} />
          <Route path="/experiences" element={<ExperiencesPage />} />
          <Route path="/experiences/:slug" element={<ExperienceDetailPage />} />
          {/* Rewards preview — public catalog of partner brands that
              can be triggered as guest rewards. Route renamed from
              `/dining` → `/rewards-preview` (2026-07-03) to align with
              the automated-guest-rewards positioning (we don't sell
              gift cards, we preview which partner rewards a booking
              can trigger). Legacy `/dining` kept as a passthrough so
              old marketing links, bookmarks, and partner redirects
              continue to resolve. */}
          <Route path="/rewards-preview" element={<DiningPage />} />
          <Route path="/dining" element={<DiningPage />} />
          <Route path="/dining/success" element={<DiningCheckoutSuccessPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/partner/setup-password" element={<PartnerSetupPasswordPage />} />
          <Route path="/partner/claim/:token" element={<PartnerClaimPage />} />
          <Route path="/partner/login" element={<PartnerLoginPage />} />
          <Route path="/become-experience-partner" element={<BecomeExperiencePartnerPage />} />
          <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
          <Route path="/design-previews/editorial" element={<PreviewA_Editorial />} />
          <Route path="/design-previews/realestate-tech" element={<PreviewB_RealEstateTech />} />
          <Route path="/design-previews/earthy" element={<PreviewC_Earthy />} />
          <Route path="/design-previews/swiss-minimal" element={<PreviewD_SwissMinimal />} />
          <Route path="/design-previews/glass-bento" element={<PreviewE_GlassBento />} />
          <Route path="/design-previews/coastal-blue" element={<PreviewF_CoastalBlue />} />
          <Route path="/design-previews/electric-blue" element={<PreviewG_ElectricBlue />} />
          <Route path="/design-previews/soft-sage" element={<PreviewH_SoftSage />} />
          <Route path="/design-previews/pale-peach" element={<PreviewI_PalePeach />} />
          <Route path="/design-previews/powder-blue" element={<PreviewJ_PowderBlue />} />
          <Route path="/design-previews/linen" element={<PreviewK_Linen />} />
          <Route path="/design-previews/light-blue-current" element={<PreviewL_LightBlueCurrent />} />

          {/* Public host profile (privacy-safe) */}
          <Route path="/host/:hostId" element={<PublicHostProfilePage />} />

          {/* Public routes with Header/Footer */}
          <Route path="/search" element={<><Header /><HomePage /><Footer /></>} />
          <Route path="/property/:id" element={<><Header /><PropertyDetailPage /><Footer /></>} />
          <Route path="/location/:location" element={<><Header /><LocationPropertiesPage /><Footer /></>} />
          
          {/* Target Audience Pages */}
          <Route path="/students" element={<><Header /><StudentsPage /><Footer /></>} />
          <Route path="/medical" element={<><Header /><MedicalPage /><Footer /></>} />
          <Route path="/insurance" element={<><Header /><InsurancePage /><Footer /></>} />

          {/* Info Pages */}
          <Route path="/help-center" element={<><Header /><HelpCenterPage /><Footer /></>} />
          <Route path="/about-montreal" element={<><Header /><AboutMontrealPage /><Footer /></>} />
          <Route path="/host-responsibly" element={<><Header /><HostResponsiblyPage /><Footer /></>} />
          <Route path="/montreal-attractions" element={<><Header /><MontrealAttractionsPage /><Footer /></>} />
          <Route path="/things-to-do-montreal" element={<><Header /><ThingsToDoMontrealPage /><Footer /></>} />
          <Route path="/aircover" element={<><Header /><AirCoverPage /><Footer /></>} />
          <Route path="/safety-information" element={<><Header /><SafetyInformationPage /><Footer /></>} />
          <Route path="/cancellation-options" element={<><Header /><CancellationOptionsPage /><Footer /></>} />
          <Route path="/sitemap" element={<><Header /><SitemapPage /><Footer /></>} />
          <Route path="/newsroom" element={<><Header /><NewsroomPage /><Footer /></>} />
          <Route path="/features" element={<><Header /><FeaturesPage /><Footer /></>} />
          <Route path="/founders-letter" element={<><Header /><FoundersLetterPage /><Footer /></>} />
          <Route path="/careers" element={<><Header /><CareersPage /><Footer /></>} />
          <Route path="/investors" element={<><Header /><InvestorsPage /><Footer /></>} />
          <Route path="/host-resources" element={<><Header /><HostResourcesPage /><Footer /></>} />
          <Route path="/map-test" element={<><Header /><MapTestPage /><Footer /></>} />
          <Route path="/logo-preview" element={<LogoPreviewPage />} />
          <Route
            path="/experience-montreal"
            element={
              <>
                <Header />
                {FEATURE_FLAGS.experienceMontreal
                  ? <ExperienceMontrealPage />
                  : <ExperienceMontrealComingSoonPage />}
                <Footer />
              </>
            }
          />
          <Route path="/calendar-test" element={<CalendarTestPage />} />

          {/* Legal Pages */}
          <Route path="/terms" element={<><Header /><TermsOfService /><Footer /></>} />
          <Route path="/privacy" element={<><Header /><PrivacyPolicy /><Footer /></>} />
          <Route path="/host-agreement" element={<><Header /><HostAgreement /><Footer /></>} />

          {/* Customer-specific pages (with CustomerLayout) */}
          <Route path="/booking/success" element={
            <CustomerLayout>
              <BookingSuccessPage />
            </CustomerLayout>
          } />
          
          <Route path="/become-host" element={<BecomeHostPage />} />
          <Route path="/list-property" element={<BecomeHostPage />} />
          
          {/* Customer routes (with CustomerLayout for role-based context) */}
          <Route path="/signup" element={<><Header /><CustomerSignupPage /><Footer /></>} />
          <Route path="/login" element={<><Header /><CustomerLoginPage /><Footer /></>} />
          <Route path="/customer-login" element={<><Header /><CustomerLoginPage /><Footer /></>} />
          <Route path="/forgot-password" element={<><Header /><ForgotPasswordPage /><Footer /></>} />
          <Route path="/verify-email" element={<><Header /><VerifyEmailPage /><Footer /></>} />
          <Route path="/dashboard" element={
            <ErrorBoundary>
              <CustomerLayout>
                <CustomerDashboardPage />
              </CustomerLayout>
            </ErrorBoundary>
          } />
          <Route path="/booking-lookup" element={
            <CustomerLayout>
              <BookingLookupPage />
            </CustomerLayout>
          } />
          <Route path="/wishlists" element={
            <CustomerLayout>
              <WishlistsPage />
            </CustomerLayout>
          } />
          <Route path="/messages" element={
            <CustomerLayout>
              <MessagesPage />
            </CustomerLayout>
          } />
          
          {/* Captive Portal (no header/footer) */}
          <Route path="/captive-portal" element={<CaptivePortalPage />} />
          
          {/* WiFi Access (no header/footer - clean experience) */}
          <Route path="/wifi-access/:unitCode" element={<WiFiAccessPage />} />
          
          {/* Admin routes (SECURE - obscured URL path) */}
          <Route path="/sys-9k4x7m2n8p5w3q1r6t/login" element={<AdminLoginPage />} />
          <Route path="/sys-9k4x7m2n8p5w3q1r6t/dashboard" element={
            <AdminLayout>
              <AdminDashboardPage />
            </AdminLayout>
          } />
          
          {/* Host routes (with HostLayout for role-based context) */}
          <Route path="/host/login" element={<HostLoginPage />} />
          <Route path="/host/signup" element={<HostSignupPage />} />
          <Route path="/host/dashboard" element={
            <HostLayout>
              <HostDashboardPage />
            </HostLayout>
          } />
          <Route path="/host/messages" element={
            <HostLayout>
              <HostMessagesPage />
            </HostLayout>
          } />
          <Route path="/host/cancellation-requests" element={
            <HostLayout>
              <HostCancellationRequestsPage />
            </HostLayout>
          } />
          
          {/* Guest Profile - accessible to hosts */}
          <Route path="/guest/:guestId" element={<><Header /><GuestProfilePage /><Footer /></>} />
        </Route> {/* end <MarketplaceGate> layout route */}
        </Routes>
        </Suspense>
        </BrandProvider>
      </BrowserRouter>
      <Toaster />
      <SonnerToaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
